import { ErrorMessage, Form, Formik, type FormikProps } from 'formik';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { authAPI } from '@/lib/utils/api';
import { storage } from '@/lib/utils/storage';

/* -------------------------------------------------------------------------- */
/* Schema                                                                      */
/* -------------------------------------------------------------------------- */

const LoginSchema = Yup.object({
  email: Yup.string().email('Please enter a valid email address').required('Email is required'),
  password: Yup.string().required('Password is required'),
});

/* -------------------------------------------------------------------------- */
/* Types                                                                       */
/* -------------------------------------------------------------------------- */

interface LoginValues {
  email: string;
  password: string;
}

interface LoginFormProps extends React.HTMLAttributes<HTMLDivElement> {}

interface LoginFormFieldsProps extends FormikProps<LoginValues> {
  loading: boolean;
}

/* -------------------------------------------------------------------------- */
/* LoginForm                                                                  */
/* -------------------------------------------------------------------------- */

export function LoginForm({ className, ...props }: LoginFormProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Welcome back</CardTitle>
        </CardHeader>

        <CardContent>
          <Formik<LoginValues>
            initialValues={{ email: '', password: '' }}
            validationSchema={LoginSchema}
            validateOnMount
            onSubmit={async (values, { setSubmitting }) => {
              setLoading(true);
              try {
                const response = await authAPI.login(values.email, values.password);

                const { accessToken, refreshToken } = response.data;

                storage.set('token', accessToken);
                storage.set('refresh_token', refreshToken);

                toast.success('Login successful');
                navigate('/dashboard');
              } catch (error: unknown) {
                const message = error instanceof Error ? error.message : 'Login failed';

                toast.error(message);
              } finally {
                setLoading(false);
                setSubmitting(false);
              }
            }}
          >
            {(formikProps) => (
              <Form className="space-y-4">
                <LoginFormFields {...formikProps} loading={loading} />
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>

      <p className="px-6 text-center text-sm text-muted-foreground">
        By clicking continue, you agree to our{' '}
        <a href="#" className="underline">
          Terms of Service
        </a>{' '}
        and{' '}
        <a href="#" className="underline">
          Privacy Policy
        </a>
        .
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* LoginFormFields                                                            */
/* -------------------------------------------------------------------------- */

function LoginFormFields({
  values,
  errors,
  touched,
  isValid,
  isSubmitting,
  loading,
  handleChange,
  handleBlur,
  submitCount,
}: LoginFormFieldsProps) {
  const emailRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  useEffect(() => {
    if (submitCount > 0) {
      if (errors.email) {
        emailRef.current?.focus();
      } else if (errors.password) {
        passwordRef.current?.focus();
      }
    }
  }, [submitCount, errors]);

  return (
    <>
      {/* Email */}
      <div className="space-y-1">
        <Label htmlFor="email">Email</Label>
        <Input
          ref={emailRef}
          id="email"
          name="email"
          type="email"
          placeholder="Email address"
          value={values.email}
          onChange={handleChange}
          onBlur={handleBlur}
          className={cn(
            touched.email && errors.email && 'border-destructive focus-visible:ring-destructive'
          )}
        />
        <ErrorMessage
          name="email"
          component="p"
          className="text-[13px] leading-4 text-destructive"
        />
      </div>

      {/* Password */}
      <div className="space-y-1">
        <Label htmlFor="password">Password</Label>
        <div className="relative">
          <Input
            ref={passwordRef}
            id="password"
            name="password"
            placeholder="Password"
            type={showPassword ? 'text' : 'password'}
            value={values.password}
            onChange={handleChange}
            onBlur={handleBlur}
            className={cn(
              touched.password &&
                errors.password &&
                'border-destructive focus-visible:ring-destructive',
              'pr-10'
            )}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute inset-y-0 right-2 flex items-center text-muted-foreground"
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <ErrorMessage
          name="password"
          component="p"
          className="text-[13px] leading-4 text-destructive"
        />
      </div>

      {/* Submit */}
      <Button type="submit" className="w-full" disabled={!isValid || isSubmitting || loading}>
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Signing in…
          </>
        ) : (
          'Login'
        )}
      </Button>
    </>
  );
}
